import { createSlice } from "@reduxjs/toolkit";
import { LoadState, User } from "../types/commonTypes";
import { CognitoIdToken } from "amazon-cognito-identity-js";
import { AuthState } from "../types/authTypes";
import sessionApi from "../api/sessionApi";

interface SessionState {
  loadState: LoadState;
  authState: AuthState;
  loadUserState: LoadState;
  user: Partial<User>;
  loadedUser: Partial<User>;
}

const initialState: SessionState = {
  loadState: LoadState.INIT,
  authState: AuthState.INIT,
  loadUserState: LoadState.INIT,
  user: {},
  loadedUser: {},
};

export const sessionSlice = createSlice({
  name: "SessionState",
  initialState,
  reducers: {
    setCurrentUser: (state, action) => {
      state.user = action.payload.user;
      state.authState =
        action.payload.user === null
          ? AuthState.UNAUTHENTICATED
          : AuthState.AUTHENTICATED;
    },
    updateUserFromCachedSession: (state, action) => {
      const idTokenPayload = action.payload.idToken.decodePayload();
      state.loadState = LoadState.LOADED;
      state.authState = AuthState.AUTHENTICATED;
      state.user = {
        ...state.user,
        idToken: action.payload.idToken.getJwtToken(),
        accessToken: action.payload.accessToken.getJwtToken(),
        refreshToken: action.payload.refreshToken.getToken(),
        username: idTokenPayload["cognito:username"],
        firstName: idTokenPayload.given_name,
        lastName: idTokenPayload.family_name,
        role: idTokenPayload["custom:role"],
      };
    },
    setAuthState: (state, action) => {
      state.authState = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        sessionApi.endpoints.exchangeCodeForToken.matchPending,
        (state) => {
          state.loadState = LoadState.LOADING;
        }
      )
      .addMatcher(
        sessionApi.endpoints.exchangeCodeForToken.matchFulfilled,
        (state, { payload }) => {
          const idTokenPayload = new CognitoIdToken({
            IdToken: payload.id_token,
          }).decodePayload();
          state.loadState = LoadState.LOADED;
          state.authState = AuthState.AUTHENTICATED;
          state.user = {
            ...state.user,
            idToken: payload.id_token,
            accessToken: payload.access_token,
            refreshToken: payload.refresh_token,
            username: idTokenPayload["cognito:username"],
            firstName: idTokenPayload.given_name,
            lastName: idTokenPayload.family_name,
            role: idTokenPayload["custom:role"],
          };
        }
      )
      .addMatcher(
        sessionApi.endpoints.exchangeCodeForToken.matchRejected,
        (state) => {
          state.loadState = LoadState.ERROR;
        }
      )
      .addMatcher(sessionApi.endpoints.loadUser.matchPending, (state) => {
        state.loadUserState = LoadState.LOADING;
      })
      .addMatcher(
        sessionApi.endpoints.loadUser.matchFulfilled,
        (state, { payload }) => {
          state.loadUserState = LoadState.LOADED;
          state.authState = AuthState.AUTHENTICATED;
          state.loadedUser = payload;
        }
      )
      .addMatcher(sessionApi.endpoints.loadUser.matchRejected, (state) => {
        state.loadUserState = LoadState.ERROR;
      });
  },
});

export const { updateUserFromCachedSession, setAuthState } =
  sessionSlice.actions;

export default sessionSlice.reducer;
